import cx from 'classnames';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {resizeTextarea} from '~/SignUpSheets/utils/misc';
import {Input} from '~library/atoms/Input';
import {Typography} from '~library/atoms/Typography';
import {sanitizeString} from '~library/utils/sanitize_html';
import {RichTextField} from './RichTextField';

import './TextField.sass';

export const TextField = ({
  uuid,
  onChange,
  onBlur,
  schema = {
    placeholder: '',
    rich: false,
    name: '',
    // biome-ignore lint/style/useNamingConvention: existing error
    max_chars: 200,
  },
  value,
  className,
  errors,
  disabled,
  'data-qa-id': dataQaId,
  isInEditMode = false,
  newOption = false,
  required = false,
  isDesktop = false,
  rich = !!schema.rich,
}) => {
  const inputRef = useRef(null);
  const debouncedResizeTextArea = useRef(debounce(resizeTextarea, 250)).current;

  const [pristine, setPristine] = useState(true);

  useEffect(() => {
    // Don't update pristine state if we're still focused in the field
    if (!inputRef.current || inputRef.current === document.activeElement) return;
    if (value) setPristine(false);
  }, [value]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    debouncedResizeTextArea(inputRef.current);
  }, [inputRef.current, disabled, isDesktop]);

  const handleBlur = useCallback(
    (e) => {
      if (e.target.value) setPristine(false);
      onBlur();
    },
    [onBlur]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const input = useMemo(() => {
    const placeholder = `${schema.placeholder}${required ? '*' : ''}`;
    const error = errors?.[0]?.msg;
    if (rich) {
      return (
        <RichTextField
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          maxLength={schema.max_chars}
          error={error}
          id={`${schema.name}-${uuid}`}
          dataQaId={dataQaId}
          label={schema.display_name}
        />
      );
    }
    return (
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder}
        maxLength={schema.max_chars}
        className="item-field"
        inputClassName="signup-sheets__text-textarea text__textarea"
        type="textarea"
        id={dataQaId}
        data-qa-id={dataQaId}
        onInput={() => resizeTextarea(inputRef.current)}
        rows={1}
        ref={inputRef}
        error={error}
        disabled={disabled}
        live
        pristine={pristine}
        variant={isDesktop ? 'paragraph1' : 'paragraph2'}
      />
    );
  }, [
    value,
    onChange,
    handleBlur,
    isInEditMode,
    newOption,
    schema,
    required,
    dataQaId,
    errors,
    isDesktop,
    disabled,
    rich,
    pristine,
    uuid,
  ]);

  if (disabled && !value) return null;

  return (
    <div className={cx(`${schema.name}-wrapper`, {[className]: !!className})}>
      {disabled
        ? <Typography variant="paragraph3" color="pebble" data-qa-id={dataQaId}>
            {sanitizeString(value)}
          </Typography>
        : input}
    </div>
  );
};

TextField.propTypes = {
  uuid: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  schema: PropTypes.shape({
    placeholder: PropTypes.string,
    rich: PropTypes.bool,
    name: PropTypes.string,
    // biome-ignore lint/style/useNamingConvention: existing error
    max_chars: PropTypes.number,
  }),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  errors: PropTypes.arrayOf(
    PropTypes.shape({
      loc: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
      msg: PropTypes.string.isRequired,
    })
  ),
  disabled: PropTypes.bool,
  'data-qa-id': PropTypes.string,
  isInEditMode: PropTypes.bool,
  newOption: PropTypes.bool,
  required: PropTypes.bool,
  isDesktop: PropTypes.bool,
  rich: PropTypes.bool,
};
